/* eslint-disable @typescript-eslint/ban-types */
import { Box, Typography, TextField } from '@mui/material';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import SmartDisplayOutlinedIcon from '@mui/icons-material/SmartDisplayOutlined';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import Button from '@ui-elements/Button';
import { useState, useEffect } from 'react';
import BasicModal from '~/components/ui-parts/BasicModal';
import { FONT_SIZES, PALETTE } from '~/assets/styles/base';

const CustomModalHeader = styled(Box)`
  padding: 30px 51px 24px 51px;
`;

const StyledIcon = styled(WarningRoundedIcon)`
  font-size: 2.4rem;
  margin-right: 6px;
`;

const Caution = styled.div`
  display: flex;
  align-items: center;
  font-size: ${FONT_SIZES.body3};
  color: ${PALETTE.error.main};
  margin-top: 12px;
  width: 370px;
  line-height: 1.5em;
`;

const VideoUploadContainer = styled.div`
  height: 226px;
  text-align: center;
  padding-top: 70px;
`;

const UrlTextField = styled(TextField)`
  width: 640px;
  height: 48px;
  margin-top: 20px;

  input[type='url'] {
    padding: 12px 20px;
    font-size: 16px;
    border: 1px solid ${PALETTE.border.frame};
  }
`;

const ImagePreviewContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 10px;
  gap: 10px;
  flex-wrap: wrap;
  max-height: 170px;
`;

type Props = {
  isOpen: boolean;
  handleClose: React.MouseEventHandler<HTMLButtonElement>;
  handleSetting: Function;
  videoUrl?: string;
};

const VideoUploadModal: React.FC<Props> = ({
  isOpen,
  handleClose,
  handleSetting,
  videoUrl,
}: Props): JSX.Element => {
  const { t } = useTranslation();

  const [enteredVideoUrl, setEnteredVideoUrl] = useState(videoUrl);

  const [labelValue, setLabelValue] = useState<JSX.Element>();
  const [modalPreviewData, setModalPreviewData] = useState<JSX.Element>();

  const modalTitle = (
    <CustomModalHeader>
      <Typography variant="title1" sx={{ display: 'flex', alignItems: 'center', fontSize: '24px' }}>
        <SmartDisplayOutlinedIcon sx={{ fontSize: '24px' }} />
        {t('registration.videoUploadModal.title')}
      </Typography>
      <Caution>
        <StyledIcon />
        {t('registration.videoUploadModal.headerNote')}
      </Caution>
    </CustomModalHeader>
  );

  const setPreviewAndLabel = (enteredUrl: string) => {
    const youtubeUrlRegExps = [
      /https:\/\/www\.youtube\.com\/watch\?v=(\w+)/,
      /https:\/\/youtu\.be\/(\w+)/,
      /https:\/\/www\.youtube\.com\/live\/(\w+)\?.*/,
    ];

    let videoId = '';
    // eslint-disable-next-line no-restricted-syntax
    for (const regExp of youtubeUrlRegExps) {
      const match = enteredUrl.match(regExp);
      if (match) {
        videoId = match[1];
        break;
      }
    }

    if (videoId) {
      const src = `https://www.youtube.com/embed/${videoId}`;
      const iFrameData = (
        <iframe
          height="170px"
          src={src}
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        />
      );
      setModalPreviewData(iFrameData);

      const iFrameDataForLabel = (
        <iframe
          width="560"
          height="315"
          src={src}
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        />
      );
      setLabelValue(iFrameDataForLabel);
      setEnteredVideoUrl(enteredUrl);
    }
  };

  const handleSettingClick = async () => {
    if (enteredVideoUrl) {
      setPreviewAndLabel(enteredVideoUrl);
    }

    handleSetting(enteredVideoUrl, labelValue);
  };

  const settingButton = (
    <div key="ModalFooterInner">
      <Button
        variant="contained"
        onClick={handleSettingClick}
        colorType={PALETTE.primary.main}
        width="110px"
        dataTestId="video-upload-close"
        sx={{ marginTop: '40px', marginBottom: '40px' }}
      >
        {t('appCommon.setting')}
      </Button>
    </div>
  );

  useEffect(() => {
    if (enteredVideoUrl) {
      setPreviewAndLabel(enteredVideoUrl);
    }
  }, []);

  return (
    <BasicModal
      width="880px"
      open={isOpen}
      titleProps={{
        customModalTitle: modalTitle,
      }}
      handleClose={handleClose}
      dataTestId="video-upload-modal"
      buttons={[settingButton]}
      aboveButtons={
        modalPreviewData ? (
          <ImagePreviewContainer data-testid="video-modal-preview">
            {modalPreviewData}
          </ImagePreviewContainer>
        ) : undefined
      }
    >
      <VideoUploadContainer data-testid="video-upload-container">
        <Typography sx={{ fontSize: '16px' }}>
          {t('registration.videoUploadModal.urlInstruction')}
        </Typography>

        <UrlTextField
          inputProps={{ 'data-testid': 'url-component-textfield' }}
          onBlur={(event) => {
            setPreviewAndLabel(event.target.value);
          }}
          onChange={(event) => {
            setEnteredVideoUrl(event.target.value);
          }}
          sx={{ input: { color: '#032B4D' } }}
          type="url"
          value={enteredVideoUrl}
        />
      </VideoUploadContainer>
    </BasicModal>
  );
};

export default VideoUploadModal;
